/**
 * Lightweight company-name normalization for matching news entity names to suppliers.
 */

const SUFFIX_RE =
  /\b(incorporated|inc\.?|ltd\.?|limited|llc\.?|l\.?l\.?c\.?|plc\.?|gmbh|ag|sa|s\.?a\.?|bv|b\.?v\.?|nv|n\.?v\.?|co\.?|company|corp\.?|corporation|se|s\.?e\.?|spa|s\.?p\.?a\.?|srl|s\.?r\.?l\.?|pty|pvt|private)\b/gi

export function normalizeCompanyName(name: string | null | undefined): string {
  if (name == null) return ''
  let s = String(name).trim().toLowerCase()
  if (!s) return ''
  s = s.replace(/["""']/g, '')
  s = s.replace(/[.,/#!$%^&*;:{}=_`~()[\]]/g, ' ')
  s = s.replace(SUFFIX_RE, ' ')
  s = s.replace(/\s+/g, ' ').trim()
  return s
}

/** Build a map that indexes suppliers under exact, lowercased, and normalized names. */
export function buildSupplierNameIndex(
  rows: Array<{ id: string; name: string }>
): Map<string, string> {
  const map = new Map<string, string>()
  const setIfAbsent = (key: string, id: string) => {
    if (!key || map.has(key)) return
    map.set(key, id)
  }
  for (const row of rows) {
    if (!row?.name || !row.id) continue
    const trimmed = row.name.trim()
    setIfAbsent(trimmed, row.id)
    setIfAbsent(trimmed.toLowerCase(), row.id)
    setIfAbsent(normalizeCompanyName(trimmed), row.id)
  }
  return map
}

/**
 * Resolve supplier id for an entity name.
 * Exact / case-insensitive / normalized first; then unambiguous contains match.
 */
export function matchSupplierId(
  entityName: string,
  nameToId: Map<string, string>,
  supplierNames?: Array<{ id: string; name: string }>
): string | null {
  const trimmed = entityName.trim()
  if (!trimmed) return null
  const direct =
    nameToId.get(trimmed) ??
    nameToId.get(trimmed.toLowerCase()) ??
    nameToId.get(normalizeCompanyName(trimmed))
  if (direct) return direct

  if (!supplierNames?.length) return null
  const needle = normalizeCompanyName(trimmed)
  if (!needle || needle.length < 3) return null

  const hits: string[] = []
  for (const s of supplierNames) {
    const hay = normalizeCompanyName(s.name)
    if (!hay) continue
    if (hay === needle || hay.includes(needle) || needle.includes(hay)) {
      if (!hits.includes(s.id)) hits.push(s.id)
    }
  }
  // Only auto-link when unambiguous
  return hits.length === 1 ? hits[0] : null
}
